import React, { useEffect, useContext } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import ShopsContext from '../../context/shops/shopsContext';

function AddOtoshopProductModal({ show, handleClose, selectedProduct }) {
  const shopsContext = useContext(ShopsContext);
  const { addProduct, updateProduct, products, listProducts } = shopsContext;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm();

  // Effect to list products when the modal is opened
  useEffect(() => {
    listProducts();
    if (selectedProduct) {
      // Prefill the form when a selected product is passed
      setValue('productName', selectedProduct.itemName);
      setValue('price', selectedProduct.price);
    }
  }, [selectedProduct, setValue, listProducts]);

  // Watch the product selection for dynamic prefill
  const selectedProductId = watch('productId');

  useEffect(() => {
    if (selectedProductId) {
      const product = products.find((p) => p.itemCode === selectedProductId);
      if (product) {
        setValue('productName', product.itemName);
        setValue('price', product.price);
      }
    } else {
      setValue('productName', ''); // Reset the fields if no product is selected
      setValue('price', '');
    }
  }, [selectedProductId, setValue, products]);

  const handleFormSubmit = async (data) => {
    try {
      if (selectedProduct) {
        // Update the existing product, only itemName and price are updated
        await updateProduct({
          itemCode: selectedProduct.itemCode, // Ensure the itemCode is passed for identification
          itemName: data.productName,
          price: parseFloat(data.price),
        });
      } else {
        // Add new product
        await addProduct({
          itemName: data.productName,
          price: parseFloat(data.price),
        });
      }
      reset(); // Reset the form after submission
      handleClose(); // Close the modal
    } catch (error) {
    }
  };

  const handleModalClose = () => {
    reset(); // Reset the form when the modal is closed
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>{selectedProduct ? 'Update Product' : 'Add New Product'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Body>
          {/* Dropdown for selecting existing product */}
          {!selectedProduct && (
            <Form.Group controlId="formProductSelect" className="mb-3">
              <Form.Label>Select Existing Product</Form.Label>
              <Form.Control as="select" {...register('productId')}>
                <option value="">-- Select a product --</option>
                {products.map((product) => (
                  <option key={product.itemCode} value={product.itemCode}>
                    {product.itemName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}

          {/* Product Name Input */}
          <Form.Group controlId="formProductName" className="mb-3">
            <Form.Label>Product Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter product name"
              {...register('productName', { required: 'Product name is required' })}
              isInvalid={!!errors.productName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.productName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Price Input */}
          <Form.Group controlId="formProductPrice" className="mb-3">
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter price"
              {...register('price', {
                required: 'Price is required',
                valueAsNumber: true,
                min: { value: 0, message: 'Price must be greater than or equal to 0' },
              })}
              isInvalid={!!errors.price}
            />
            <Form.Control.Feedback type="invalid">
              {errors.price?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button type="submit" variant="primary">
            {selectedProduct ? 'Update Product' : 'Save Product'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddOtoshopProductModal;
